import type { SessionInfo } from "@opencode/client/promise"
import { Button } from "@opencode/ui/button"
import { useDialog } from "@opencode/ui/context/dialog"
import { Dialog, DialogFooter, DialogHeader, DialogTitleGroup } from "@opencode/ui/dialog"
import { Icon } from "@opencode/ui/icon"
import { IconButton } from "@opencode/ui/icon-button"
import { InlineInput } from "@opencode/ui/inline-input"
import { Menu } from "@opencode/ui/menu"
import { Tooltip } from "@opencode/ui/tooltip"
import { getFilename } from "@opencode/util/path"
import { Key } from "@solid-primitives/keyed"
import { skipToken, useQuery, useQueryClient } from "@tanstack/solid-query"
import { useNavigate } from "@solidjs/router"
import {
  createEffect,
  createMemo,
  createUniqueId,
  For,
  type Accessor,
  mapArray,
  mergeProps,
  Match,
  onCleanup,
  Show,
  startTransition,
  Switch,
} from "solid-js"
import {
  HOME_SESSION_LIMIT,
  loadHomeSessionIndex,
  mergeHomeSessionIndex,
  retainHomeSessions,
} from "@/home/sessions/index"
import { shouldOpenSessionInBackground } from "@/home/sessions/open"
import { fileManagerApp } from "@/home/projects/file-manager"
import { buildHomeSessionRecords, homeProjectForSession, type HomeSessionRecord } from "@/home/sessions/records"
import { useLanguage } from "@/runtime/i18n/language"
import { usePlatform } from "@/runtime/platform/platform"
import { serverName, ServerConnection } from "@/runtime/server/registry"
import { fixedRemoteProjectID } from "@/servers/ssh/name"
import { RemoteWorkspaceMenuItem } from "@/runtime/server/remote"
import { useGlobal, useServerCtx } from "@/runtime/server/runtime"
import { closeHomeProject, displayName, errorMessage } from "@/shell/layout/helpers"
import { ProjectIcon } from "@/shell/layout/project-icon"
import { useSessionTabAvatarState } from "@/shell/layout/project-avatar-state"
import { dismissToast, showToast } from "@/shell/notifications/toast"
import { useProjectEntry } from "@/shell/project-entry"
import { type LocalProject, useLayout } from "@/shell/state/layout"
import { useTabs } from "@/shell/tabs/tabs"
import { pathKey, type PathKey } from "@/workspaces/path-key"
import { containsDirectory, sameDirectory } from "@/workspaces/paths"
import { useRenameProject } from "@/workspaces/rename"
import { createStore } from "solid-js/store"
import { useSidebar } from "./sidebar/context"
import { sidebarKey, type SidebarProjectRef, type SidebarRef } from "./sidebar/schema"
import { orderSidebarItems, sortSidebarProjects } from "./sidebar/state"
import { SidebarDragItem, SidebarDropZone, type SidebarDrop } from "./sidebar/drag"
import {
  SessionNavigationRow,
  SidebarPinIcon,
  navigationStatusLabel,
  sessionSidebarRef,
  type NavigationStatus,
} from "./sidebar/session-row"
import { SidebarSortMenu } from "./sidebar/sort-menu"
import { SidebarMenuPortal } from "./sidebar/menu"
import { sidebarAttentionPriority } from "./sidebar/attention"
import "./session-navigation-list.css"

// Match Home's query selection so the shared cache stays opaque to Solid Query.
const selectSessions = (sessions: SessionInfo[]) => () => sessions

type SessionNavigationListProps = {
  enabled?: boolean
  onNavigate?: () => void
  pinnedProject?: SidebarProjectRef
  pinnedOrder?: SidebarRef[]
}

export function SessionNavigationList(props: SessionNavigationListProps) {
  const layout = useLayout()
  const tabs = useTabs()
  const global = useGlobal()
  const activeServer = createMemo(() => {
    const route = layout.route()
    const key =
      route.type === "session"
        ? route.server
        : route.type === "draft"
          ? tabs.store.find((tab) => tab.type === "draft" && tab.draftID === route.draftID)?.server
          : layout.home.selection().server
    return global.servers.list().find((conn) => ServerConnection.key(conn) === key) ?? global.servers.list()[0]
  })
  const sidebar = useSidebar()
  const contentID = createUniqueId()
  const serverKeys = createMemo(() => {
    if (props.pinnedProject) return [props.pinnedProject.server]
    const keys = global.servers
      .list()
      .filter((conn) => conn === activeServer() || global.ensureServerCtx(conn).projects.list().length > 0)
      .map(ServerConnection.key)
    return keys.length ? keys : [undefined]
  })
  const lists = mapArray(serverKeys, (key) =>
    createServerProjectList(
      mergeProps(props, {
        get connection() {
          return global.servers.list().find((conn) => ServerConnection.key(conn) === key)
        },
        get soleServer() {
          return serverKeys().length === 1
        },
        get activeServer() {
          return activeServer()
        },
        contentID,
        get projectOrder(): SidebarProjectRef[] {
          return projectOrder()
        },
      }),
    ),
  )
  const groups = createMemo(() => {
    const items = lists().flatMap((list) =>
      list.groups().map((group) => ({
        group,
        list,
        ref: list.projectRef(group.project),
      })),
    )
    if (props.pinnedProject) return items
    return sortSidebarProjects(items, {
      key: (item) => sidebarKey(item.ref),
      server: (item) => item.ref.server,
      updated: (item) => item.group.updated,
      name: (item) => item.group.name,
      order: sidebar.projectListSort() === "manual" ? sidebar.projectListOrder() : [],
      legacy: sidebar.projectListSort() === "manual" ? sidebar.projectOrder : () => [],
    })
  })
  const projectOrder = createMemo(() => groups().map((item) => item.ref))
  return (
    <div data-component={props.pinnedProject ? "pinned-project-list" : "session-navigation-list"}>
      <Show when={!props.pinnedProject}>{lists()[0]?.Heading()}</Show>
      <div id={contentID} hidden={!props.pinnedProject && !sidebar.state.projectsExpanded}>
        <For each={lists()}>{(list) => list.Status()}</For>
        <Show when={groups().length > 0} fallback={lists()[0]?.Empty()}>
          <Key each={groups()} by={(item) => sidebarKey(item.ref)}>
            {(item) => {
              const Project = item().list.Project
              return <Project group={() => item().group} />
            }}
          </Key>
        </Show>
      </div>
    </div>
  )
}

function createServerProjectList(
  props: SessionNavigationListProps & {
    connection?: ServerConnection.Any
    activeServer?: ServerConnection.Any
    soleServer: boolean
    projectOrder: SidebarProjectRef[]
    contentID: string
  },
) {
  const layout = useLayout()
  const tabs = useTabs()
  const global = useGlobal()
  const language = useLanguage()
  const platform = usePlatform()
  const navigate = useNavigate()
  const projectEntry = useProjectEntry()
  const renameProject = useRenameProject()
  const dialog = useDialog()
  const queryClient = useQueryClient()
  const sidebar = useSidebar()
  const server = createMemo<ServerConnection.Any | undefined>(() => {
    if (props.pinnedProject)
      return global.servers.list().find((item) => ServerConnection.key(item) === props.pinnedProject!.server)
    return props.connection
  })
  const context = useServerCtx(server)
  const projects = createMemo(() => context()?.projects.list() ?? [])
  const projectRef = (project: LocalProject): SidebarProjectRef => ({
    type: "project",
    server: ServerConnection.key(server()!),
    directory: project.worktree,
    projectID: project.id,
    title: displayName(project),
  })
  const connected = () => context()?.sdk.connection.status() === "connected"
  const localServer = createMemo(() => {
    if (platform.platform !== "desktop") return props.activeServer ?? server()
    const conn = props.activeServer ?? server()
    return ServerConnection.local(conn)
      ? conn
      : (global.servers.list().find(ServerConnection.builtin) ?? global.servers.list().find(ServerConnection.local))
  })
  const canAddProject = (conn?: ServerConnection.Any) =>
    !!conn && global.ensureServerCtx(conn).sdk.connection.status() === "connected"
  const serverLabel = () => {
    const conn = server()
    if (!props.soleServer || !conn || ServerConnection.local(conn)) return
    if (conn.type === "remote" || (conn.type === "ssh" && conn.id === fixedRemoteProjectID)) return
    return serverName(conn)
  }
  const sessionLoad = useQuery(() => {
    const ctx = context()
    return {
      queryKey: ["home-sessions", server()] as const,
      // Navigation shortcuts use this metadata even while the sidebar is collapsed.
      enabled: !!ctx && connected(),
      queryFn: ctx
        ? ({ signal }) => loadHomeSessionIndex((input, options) => ctx.sdk.api.session.list(input, options), signal)
        : skipToken,
      retry: false,
      staleTime: 30_000,
      refetchOnMount: true,
      refetchOnReconnect: true,
      select: selectSessions,
    }
  })
  createEffect(() => {
    const conn = server()
    const ctx = context()
    if (!conn || !ctx) return
    const queryKey = ["home-sessions", conn] as const
    onCleanup(
      ctx.data.on("session.deleted", (event) => {
        // The session store removes its tombstone once deletion is confirmed.
        // Evict the shared index row too, so that cached metadata cannot restore it.
        queryClient.setQueryData<SessionInfo[]>(queryKey, (current) =>
          current?.filter((session) => session.id !== event.data.sessionID),
        )
        void queryClient.invalidateQueries({ queryKey, exact: true })
      }),
    )
  })
  const currentSession = createMemo(() => {
    const route = layout.route()
    if (route?.type !== "session" || !server() || route.server !== ServerConnection.key(server()!)) return
    return context()?.data.session.root(route.sessionId) ?? route.sessionId
  })
  const preserved = createMemo(() => {
    const ctx = context()
    const ids = new Set<string>()
    const current = currentSession()
    if (current) ids.add(current)
    if (!ctx) return ids
    for (const session of ctx.data.session.list()) {
      if (
        ctx.data.session.status(session.id) === "running" ||
        ctx.data.session.permission.list(session.id)?.length ||
        ctx.data.session.form.list(session.id)?.length ||
        ctx.notification.session.unseenCount(session.id) > 0
      )
        ids.add(ctx.data.session.root(session.id))
    }
    return ids
  })
  const records = createMemo(() => {
    const ctx = context()
    if (!ctx) return []
    const fetched = sessionLoad.isPending ? [] : (sessionLoad.data?.() ?? [])
    const merged = ctx.data.session.apply(mergeHomeSessionIndex(fetched, ctx.data.session.list()))
    const retained = retainHomeSessions(merged, HOME_SESSION_LIMIT, Date.now())
    const keep = preserved()
    const all = buildHomeSessionRecords({
      sessions: () => [...retained, ...merged.filter((session) => keep.has(session.id))],
      projectDirectories: () => undefined,
      projects,
    })
    // The recent limit must never hide the current session or known work that
    // needs attention. Status only affects inclusion, never the list's order.
    return all.filter((record, index) => index < HOME_SESSION_LIMIT || keep.has(record.session.id))
  })
  const activity = createMemo(() => {
    const ctx = context()
    const updated = new Map<PathKey, number>()
    if (!ctx) return updated
    const fetched = sessionLoad.isPending ? [] : (sessionLoad.data?.() ?? [])
    const sessions = ctx.data.session.apply(mergeHomeSessionIndex(fetched, ctx.data.session.list()))
    for (const session of sessions) {
      const key = pathKey(homeProjectForSession(session, projects())?.worktree ?? session.location.directory)
      updated.set(key, Math.max(updated.get(key) ?? 0, session.time.updated ?? session.time.created))
    }
    return updated
  })
  const currentProject = createMemo<LocalProject | undefined>(() => {
    const ctx = context()
    const route = layout.route()
    if (route?.type === "session") {
      if (!server() || route.server !== ServerConnection.key(server()!)) return
      const session = ctx?.data.session.get(route.sessionId)
      if (session)
        return (
          homeProjectForSession(session, projects()) ??
          ctx?.projects.resolve({ worktree: session.location.directory, expanded: false })
        )
      return records().find((record) => record.session.id === currentSession())?.project
    }
    if (route?.type === "draft") {
      const tab = tabs.store.find((tab) => tab.type === "draft" && tab.draftID === route.draftID)
      if (tab?.type !== "draft" || !server() || tab.server !== ServerConnection.key(server()!)) return
      if (tab.pendingProject) return
      const directory = pathKey(tab.directory)
      return (
        projects().find(
          (project) =>
            pathKey(project.worktree) === directory ||
            project.sandboxes?.some((sandbox) => pathKey(sandbox) === directory),
        ) ?? ctx?.projects.resolve({ worktree: tab.directory, expanded: false })
      )
    }
    const selection = layout.home.selection()
    if (!server() || selection.server !== ServerConnection.key(server()!)) return
    const directory = selection.directory
    return directory ? projects().find((project) => pathKey(project.worktree) === pathKey(directory)) : undefined
  })
  const attentionStates = mapArray(
    () => records().map((record) => record.session.id),
    (sessionID) => {
      const state = useSessionTabAvatarState(
        () => ServerConnection.key(server()!),
        () => sessionID,
        () => true,
      )
      return { id: sessionID, priority: () => sidebarAttentionPriority(state) }
    },
  )
  const attentionByID = createMemo(() => new Map(attentionStates().map((state) => [state.id, state.priority])))
  const groups = createMemo(() => {
    const catalog = new Map<PathKey, LocalProject>(projects().map((project) => [pathKey(project.worktree), project]))
    const ctx = context()
    const closed = ctx?.projects.closed() ?? []
    const closedPaths = new Set(closed.map(pathKey))
    // A session can live in a worktree outside the removed root directory.
    const closedProjects = (ctx?.sync.data.project ?? []).filter((project) =>
      [project.worktree, ...(project.sandboxes ?? [])].some(
        (directory) => closedPaths.has(pathKey(directory)) || closed.some((root) => sameDirectory(root, directory)),
      ),
    )
    const closedIDs = new Set(closedProjects.map((project) => project.id))
    const removed = (project: LocalProject) =>
      closedPaths.has(pathKey(project.worktree)) ||
      (!!project.id && closedIDs.has(project.id)) ||
      closed.some((directory) => containsDirectory(directory, project.worktree)) ||
      closedProjects.some((item) =>
        [item.worktree, ...(item.sandboxes ?? [])].some((directory) => containsDirectory(directory, project.worktree)),
      )
    const items = Map.groupBy(records(), (record) => pathKey(record.project.worktree))
    // Project identity survives opening every row or having no recent rows.
    // Explicitly removed projects stay out of Projects even while their tabs are open.
    records().forEach(({ project }) => {
      const key = pathKey(project.worktree)
      if (!catalog.has(key) && !removed(project)) catalog.set(key, project)
    })
    const current = currentProject()
    if (current && !removed(current) && !catalog.has(pathKey(current.worktree)))
      catalog.set(pathKey(current.worktree), current)
    const pinned = props.pinnedProject
    if (pinned && !catalog.has(pathKey(pinned.directory)))
      catalog.set(pathKey(pinned.directory), {
        id: pinned.projectID,
        worktree: pinned.directory,
        name: pinned.title,
        expanded: false,
      })
    const conn = server()
    if (!conn) return []
    const result = [...catalog]
      .filter(([key, project]) => (pinned ? key === pathKey(pinned.directory) : !sidebar.isPinned(projectRef(project))))
      .map(([key, project]) => {
        const ref = projectRef(project)
        const mode = sidebar.projectSessionSort(ref)
        const records = (items.get(key) ?? []).filter(
          (record) => !sidebar.isPinned(sessionSidebarRef(ServerConnection.key(conn), record)),
        )
        const sorted =
          mode === "manual"
            ? orderSidebarItems(records, sidebar.sessionOrder(ref), (record) => record.session.id)
            : mode === "attention"
              ? records.toSorted((a, b) => attention(b.session.id) - attention(a.session.id))
              : records
        return {
          key,
          project,
          name: displayName(project),
          updated: Math.max(project.time?.updated ?? project.time?.created ?? 0, activity().get(key) ?? 0),
          records: sorted,
          hasPinnedSessions: (items.get(key)?.length ?? 0) > sorted.length,
        }
      })
    return orderSidebarItems(result, sidebar.projectOrder(ServerConnection.key(conn)), (group) =>
      sidebarKey(projectRef(group.project)),
    )
  })
  function attention(id: string) {
    return attentionByID().get(id)?.() ?? 0
  }
  const isExpanded = (key: string) => {
    const project = groups().find((group) => group.key === key)?.project
    if (!project) return false
    const current = currentProject()
    return (
      sidebar.projectExpanded(projectRef(project)) ?? key === (current ? pathKey(current.worktree) : groups()[0]?.key)
    )
  }
  type Group = ReturnType<typeof groups>[number]
  const sessionRefs = (group: Group) =>
    group.records.map((record) => sessionSidebarRef(ServerConnection.key(server()!), record))
  const sessionDrop = (group: Group, anchor?: SidebarRef): SidebarDrop => ({
    kind: "sessions",
    project: projectRef(group.project),
    order: sessionRefs(group),
    anchor,
  })
  const projectDrop = (group: Group): SidebarDrop => {
    if (props.pinnedProject)
      return { kind: "pinned", order: props.pinnedOrder ?? [], anchor: projectRef(group.project) }
    return {
      kind: "projects",
      order: props.projectOrder,
      anchor: projectRef(group.project),
      sessions: { project: projectRef(group.project), order: sessionRefs(group) },
    }
  }
  function moveProject(project: LocalProject, offset: -1 | 1) {
    const ref = projectRef(project)
    const order = props.pinnedProject ? [...(props.pinnedOrder ?? [])] : [...props.projectOrder]
    const index = order.findIndex((item) => sidebarKey(item) === sidebarKey(ref))
    if (index < 0 || index + offset < 0 || index + offset >= order.length) return
    order.splice(index + offset, 0, ...order.splice(index, 1))
    if (props.pinnedProject) sidebar.reorderPinned(order.map(sidebarKey))
    else sidebar.reorderProjectList(order.map(sidebarKey))
  }
  function moveSession(group: Group, id: string, offset: -1 | 1) {
    const order = group.records.map((record) => record.session.id)
    const index = order.indexOf(id)
    if (index < 0 || index + offset < 0 || index + offset >= order.length) return
    order.splice(index + offset, 0, ...order.splice(index, 1))
    sidebar.reorderSessions(projectRef(group.project), order)
  }

  function open(record: HomeSessionRecord, event: MouseEvent) {
    if (event.button !== 0 && event.button !== 1) return
    const conn = server()
    const ctx = context()
    if (!conn || !ctx) return
    const background = shouldOpenSessionInBackground({
      button: event.button,
      mac: /(Mac|iPod|iPhone|iPad)/.test(navigator.platform),
      meta: event.metaKey,
      ctrl: event.ctrlKey,
      shift: event.shiftKey,
      alt: event.altKey,
    })
    event.preventDefault()
    if (!background) void ctx.data.session.message.sync(record.session.id).catch(() => undefined)
    void startTransition(() => {
      const tab = tabs.addSessionTab({ server: ServerConnection.key(conn), sessionId: record.session.id })
      if (!background) tabs.select(tab)
      ctx.data.session.remember(record.session)
      ctx.projects.open(record.project.worktree)
      if (!background) ctx.projects.touch(record.project.worktree)
      if (!background) props.onNavigate?.()
    })
  }

  function focusAddedProject(conn: ServerConnection.Any, directory: string) {
    const key = ServerConnection.key(conn)
    const switched = key !== ServerConnection.key(props.activeServer ?? server() ?? conn)
    layout.home.setSelection({ server: key, directory })
    sidebar.setProjectsExpanded(true)
    const route = layout.route()
    const draft =
      route.type === "draft"
        ? tabs.store.find((tab) => tab.type === "draft" && tab.draftID === route.draftID)
        : undefined
    if (draft?.type === "draft" && draft.pendingProject) {
      tabs.updateDraft(draft.draftID, {
        server: key,
        directory,
        pendingProject: undefined,
        worktree: "main",
        branch: undefined,
      })
      return
    }
    if (switched && layout.route().type !== "home") navigate("/?view=library")
  }

  function addProject(conn?: ServerConnection.Any) {
    if (!conn || !canAddProject(conn)) return
    projectEntry.open(conn, (directory) => focusAddedProject(conn, directory))
  }

  function addProjectFromEmpty() {
    const conn = server()
    if (!conn || !connected()) return
    projectEntry.choose(conn, focusAddedProject)
  }

  function newInProject(directory: string) {
    const conn = server()
    if (!conn || !connected()) return
    const ctx = global.ensureServerCtx(conn)
    ctx.projects.touch(directory)
    void tabs.newDraft({ server: ServerConnection.key(conn), directory })
    props.onNavigate?.()
  }

  function removeProject(directory: string) {
    const conn = server()
    const ctx = context()
    if (!conn || !ctx) return
    const selection = layout.home.selection()
    const ref = { type: "project" as const, server: ServerConnection.key(conn), directory }
    const snapshot = sidebar.captureProject(ref)
    snapshot.expanded ??= isExpanded(pathKey(directory))
    const name = displayName(
      projects().find((item) => sameDirectory(item.worktree, directory)) ?? { worktree: directory },
    )
    sidebar.removeProject(ref)
    const next = closeHomeProject(selection, ServerConnection.key(conn), ctx.projects, directory)
    if (next && next !== selection) layout.home.setSelection(next)
    const toast = showToast({
      title: language.t("session.navigation.removeProject.done", { name }),
      duration: 8000,
      actions: [
        {
          label: language.t("session.navigation.undo"),
          onClick: () => {
            ctx.projects.open(directory)
            sidebar.restoreProject(snapshot)
            dismissToast(toast)
          },
        },
      ],
    })
  }

  const Heading = () => (
    <SidebarDropZone
      name="projects"
      target={{
        kind: "projects",
        order: [...props.projectOrder],
      }}
    >
      <h2 data-slot="session-navigation-projects-heading" aria-label={language.t("sair.navigation.projects")}>
        <button
          type="button"
          data-action="session-navigation-projects-toggle"
          aria-expanded={sidebar.state.projectsExpanded}
          aria-controls={props.contentID}
          onClick={() => sidebar.setProjectsExpanded(!sidebar.state.projectsExpanded)}
        >
          <span data-slot="session-navigation-projects-title">{language.t("sair.navigation.projects")}</span>
          <Icon name={sidebar.state.projectsExpanded ? "chevron-down" : "chevron-right"} size="small" />
        </button>
        <Show when={serverLabel()}>
          {(label) => (
            <span data-slot="session-navigation-server-label" title={ServerConnection.key(server()!)}>
              {label()}
            </span>
          )}
        </Show>
        <Menu placement="bottom-end" gutter={6} modal={false}>
          <Tooltip value={language.t("session.navigation.projectsOptions")} placement="bottom">
            <Menu.Trigger
              as={IconButton}
              data-action="session-navigation-projects-options"
              variant="ghost-muted"
              size="small"
              icon={<Icon name="outline-dots" size="small" />}
              aria-label={language.t("session.navigation.projectsOptions")}
            />
          </Tooltip>
          <SidebarMenuPortal>
            <Menu.Content>
              <Menu.RadioGroup value={sidebar.projectListSort()}>
                <For each={["updated", "manual"] as const}>
                  {(mode) => (
                    <Menu.RadioItem
                      value={mode}
                      closeOnSelect
                      onSelect={() => sidebar.setProjectListSort(mode, props.projectOrder.map(sidebarKey))}
                    >
                      {language.t(`session.navigation.sort.${mode}`)}
                    </Menu.RadioItem>
                  )}
                </For>
              </Menu.RadioGroup>
            </Menu.Content>
          </SidebarMenuPortal>
        </Menu>
        <Show
          when={platform.cloudWorkspace}
          fallback={
            <Menu placement="bottom-end" gutter={6} modal={false}>
              <Tooltip value={language.t("home.project.add")} placement="bottom">
                <Menu.Trigger
                  as={IconButton}
                  data-action="session-navigation-add-project"
                  variant="ghost-muted"
                  size="small"
                  icon={<Icon name="plus" size="small" />}
                  aria-label={language.t("home.project.add")}
                  disabled={!canAddProject(localServer()) && !projectEntry.canRemote()}
                />
              </Tooltip>
              <SidebarMenuPortal>
                <Menu.Content>
                  <Menu.Item disabled={!canAddProject(localServer())} onSelect={() => addProject(localServer())}>
                    <Icon
                      name={
                        platform.platform === "desktop" || ServerConnection.local(localServer())
                          ? "folder"
                          : "folder-remote"
                      }
                      size="small"
                    />
                    <span>
                      {language.t(
                        platform.platform === "desktop" ? "project.entry.addLocal" : "project.entry.addCurrentServer",
                      )}
                    </span>
                  </Menu.Item>
                  <Menu.Item
                    disabled={!projectEntry.canRemote()}
                    onSelect={() => projectEntry.openRemote(focusAddedProject)}
                  >
                    <Icon name="folder-remote" size="small" />
                    {language.t("project.entry.addRemote")}
                  </Menu.Item>
                </Menu.Content>
              </SidebarMenuPortal>
            </Menu>
          }
        >
          <Tooltip value={language.t("home.project.add")} placement="bottom">
            <IconButton
              data-action="session-navigation-add-project"
              variant="ghost-muted"
              size="small"
              icon={<Icon name="plus" size="small" />}
              aria-label={language.t("home.project.add")}
              disabled={!projectEntry.canRemote()}
              onClick={() => projectEntry.openRemote(focusAddedProject)}
            />
          </Tooltip>
        </Show>
      </h2>
    </SidebarDropZone>
  )
  const Loading = () => (
    <div
      data-slot="session-navigation-projects-skeleton"
      role="status"
      aria-label={language.t("common.loading")}
      aria-busy="true"
    >
      <For each={[0, 1, 2]}>
        {() => (
          <div data-slot="session-navigation-skeleton-row" aria-hidden="true">
            <span data-slot="session-navigation-skeleton-icon" />
            <span data-slot="session-navigation-skeleton-name" />
          </div>
        )}
      </For>
    </div>
  )
  const Status = () => (
    <Switch>
      <Match when={!server()}>
        <Show
          when={props.pinnedProject}
          keyed
          fallback={<p data-slot="session-navigation-message">{language.t("session.navigation.noServer")}</p>}
        >
          {(ref) => (
            <SidebarDragItem item={ref} target={{ kind: "pinned", order: props.pinnedOrder ?? [], anchor: ref }}>
              <div data-slot="sidebar-pin-unavailable">
                <span title={ref.directory}>{ref.title || getFilename(ref.directory)}</span>
                <span role="status">{language.t("session.navigation.unavailable")}</span>
                <button type="button" data-sidebar-no-drag onClick={() => sidebar.unpin(ref)}>
                  {language.t("session.navigation.unpinProject")}
                </button>
              </div>
            </SidebarDragItem>
          )}
        </Show>
      </Match>
      <Match when={context()?.sdk.connection.status() === "connecting"}>
        <Show when={groups().length === 0}>
          <Loading />
        </Show>
      </Match>
      <Match when={!connected()}>
        <p data-slot="session-navigation-message" role="status">
          {language.t("session.navigation.disconnected")}
        </p>
      </Match>
      <Match when={sessionLoad.isError}>
        <div data-slot="session-navigation-message" role="status">
          <p>{language.t("session.navigation.loadFailed")}</p>
          <button type="button" onClick={() => void sessionLoad.refetch()} disabled={sessionLoad.isFetching}>
            {language.t("session.navigation.retry")}
          </button>
        </div>
      </Match>
    </Switch>
  )
  const Empty = () => (
    <Show when={!!server() && connected() && !sessionLoad.isError}>
      <SidebarDropZone
        name="empty-projects"
        target={
          props.pinnedProject
            ? { kind: "pinned", order: props.pinnedOrder ?? [] }
            : { kind: "projects", order: props.projectOrder }
        }
      >
        <Show when={!sessionLoad.isPending} fallback={<Loading />}>
          <div data-slot="session-navigation-empty-projects">
            <div data-slot="session-navigation-empty-projects-label" role="status">
              <Icon name="folder" size="large" />
              <p>
                {language.t(
                  sidebar.state.pins.some((ref) => ref.type === "project")
                    ? "session.navigation.noUnpinnedProjects"
                    : "session.navigation.noProjects",
                )}
              </p>
            </div>
            <Button
              variant="neutral"
              icon="plus"
              data-action="session-navigation-empty-add-project"
              onClick={addProjectFromEmpty}
            >
              {language.t("home.project.add")}
            </Button>
          </div>
        </Show>
      </SidebarDropZone>
    </Show>
  )
  const Project = (view: { group: Accessor<Group> }) => {
    const group = view.group
    createEffect(() => sidebar.remember(projectRef(group().project)))
    const id = createUniqueId()
    let row: HTMLElement | undefined
    let heading: HTMLDivElement | undefined
    let toggle: HTMLButtonElement | undefined
    let input: HTMLInputElement | undefined
    let outside = false
    const [actions, setActions] = createStore({
      menu: undefined as { x: number; y: number } | undefined,
      editor: undefined as { draft: string; saving: boolean } | undefined,
    })
    const openMenu = (x: number, y: number) => {
      if (!heading) return
      const bounds = heading.getBoundingClientRect()
      setActions("menu", { x: x - bounds.left, y: y - bounds.top })
    }
    const openEditor = () => {
      setActions("editor", { draft: group().name, saving: false })
      requestAnimationFrame(() => {
        input?.focus()
        input?.select()
      })
    }
    const closeEditor = () => {
      if (actions.editor?.saving) return
      setActions("editor", undefined)
    }
    const saveEditor = async () => {
      if (!actions.editor || actions.editor.saving) return
      const name = actions.editor.draft.trim()
      if (!name || name === group().name) {
        closeEditor()
        requestAnimationFrame(() => toggle?.focus())
        return
      }
      const conn = server()
      if (!conn) return
      const project = group().project
      setActions("editor", "saving", true)
      const saved = await renameProject(conn, project, name).catch((cause: unknown) => {
        showToast({
          variant: "error",
          title: language.t("common.requestFailed"),
          description: errorMessage(cause, language.t("common.requestFailed")),
        })
        return undefined
      })
      const restore = document.activeElement === document.body || document.activeElement === input
      if (saved) setActions("editor", undefined)
      if (!saved) setActions("editor", "saving", false)
      if (restore)
        requestAnimationFrame(() => {
          if (!saved) return input?.focus()
          if (toggle?.isConnected) return toggle.focus()
          Array.from(document.querySelectorAll<HTMLElement>('[data-slot="session-navigation-project"]'))
            .find(
              (element) =>
                element.dataset.server === ServerConnection.key(conn) &&
                sameDirectory(element.dataset.projectDirectory ?? "", saved.directory),
            )
            ?.querySelector<HTMLElement>('[data-action="session-navigation-project-toggle"]')
            ?.focus()
        })
    }
    const remove = () => {
      const directory = group().project.worktree
      const name = group().name
      const focus =
        row?.nextElementSibling?.querySelector<HTMLElement>('[data-action="session-navigation-project-toggle"]') ??
        row?.previousElementSibling?.querySelector<HTMLElement>('[data-action="session-navigation-project-toggle"]')
      let removed = false
      void dialog.show(
        () => (
          <RemoveProjectDialog
            name={name}
            remove={() => {
              removed = true
              removeProject(directory)
            }}
          />
        ),
        () =>
          requestAnimationFrame(() => {
            const target =
              !removed && toggle?.isConnected
                ? toggle
                : focus?.isConnected
                  ? focus
                  : document.querySelector<HTMLElement>('[data-action="session-navigation-add-project"]:not(:disabled)')
            target?.focus()
          }),
      )
    }
    const copyPath = () => {
      const path = group().project.worktree
      void (platform.writeClipboardText?.(path) ?? navigator.clipboard.writeText(path)).catch((cause: unknown) =>
        showToast({
          variant: "error",
          title: language.t("common.requestFailed"),
          description: errorMessage(cause, language.t("common.requestFailed")),
        }),
      )
    }
    const reveal = () => {
      if (!platform.openPath) return
      void platform.openPath(group().project.worktree).catch((cause: unknown) =>
        showToast({
          variant: "error",
          title: language.t("common.requestFailed"),
          description: errorMessage(cause, language.t("common.requestFailed")),
        }),
      )
    }
    const menuItems = () => (
      <>
        <Menu.Item
          onSelect={() =>
            sidebar.isPinned(projectRef(group().project))
              ? sidebar.unpin(projectRef(group().project))
              : sidebar.pin(projectRef(group().project))
          }
        >
          <SidebarPinIcon unpin={sidebar.isPinned(projectRef(group().project))} />
          {language.t(
            sidebar.isPinned(projectRef(group().project))
              ? "session.navigation.unpinProject"
              : "session.navigation.pinProject",
          )}
        </Menu.Item>
        <Menu.Item
          onSelect={() => moveProject(group().project, -1)}
          disabled={
            (props.pinnedProject ? props.pinnedOrder : props.projectOrder)?.findIndex(
              (ref) => sidebarKey(ref) === sidebarKey(projectRef(group().project)),
            ) === 0
          }
        >
          <Icon name="arrow-up" size="small" />
          {language.t("session.navigation.moveUp")}
        </Menu.Item>
        <Menu.Item
          onSelect={() => moveProject(group().project, 1)}
          disabled={
            (props.pinnedProject ? props.pinnedOrder : props.projectOrder)?.findIndex(
              (ref) => sidebarKey(ref) === sidebarKey(projectRef(group().project)),
            ) ===
            (props.pinnedProject ? (props.pinnedOrder?.length ?? 0) : props.projectOrder.length) - 1
          }
        >
          <Icon name="arrow-up" size="small" class="rotate-180" />
          {language.t("session.navigation.moveDown")}
        </Menu.Item>
        <SidebarSortMenu
          mode={sidebar.projectSessionSort(projectRef(group().project))}
          onChange={(mode) => sidebar.setProjectSessionSort(projectRef(group().project), mode)}
          nested
        />
        <Menu.Separator />
        <Menu.Item onSelect={openEditor}>
          <Icon name="edit" size="small" />
          {language.t("common.rename")}
        </Menu.Item>
        <Show when={platform.platform === "desktop" && !!platform.openPath && ServerConnection.local(server())}>
          <Menu.Item onSelect={reveal}>
            <Icon name={fileManagerApp(platform.os ?? "unknown").icon} size="small" />
            {language.t(fileManagerApp(platform.os ?? "unknown").actionLabel)}
          </Menu.Item>
        </Show>
        <Menu.Item onSelect={copyPath}>
          <Icon name="copy" size="small" />
          {language.t("session.header.open.copyPath")}
        </Menu.Item>
        <RemoteWorkspaceMenuItem server={server()} />
        <Menu.Separator />
        <Menu.Item onSelect={remove}>
          <Icon name="outline-xmark" size="small" />
          {language.t("session.navigation.removeProject")}
        </Menu.Item>
      </>
    )
    // One status subscription per root serves both its row and the
    // collapsed project indicator. Collapsed rows stay unmounted.
    const states = mapArray(
      () => group().records.map((record) => record.session.id),
      (sessionId) => {
        const state = useSessionTabAvatarState(
          () => ServerConnection.key(server()!),
          () => sessionId,
          () => true,
        )
        return {
          id: sessionId,
          status: createMemo<NavigationStatus>(() => {
            if (state.needsAttention()) return "attention"
            if (state.loading()) return "running"
            if (state.unread()) return "unread"
            return "idle"
          }),
        }
      },
    )
    const stateById = createMemo(() => new Map(states().map((state) => [state.id, state.status])))
    const status = createMemo<NavigationStatus>(() => {
      const values = states().map((state) => state.status())
      if (values.includes("attention")) return "attention"
      if (values.includes("running")) return "running"
      if (values.includes("unread")) return "unread"
      return "idle"
    })
    return (
      <section
        data-slot="session-navigation-project"
        data-project-directory={group().project.worktree}
        data-server={ServerConnection.key(server()!)}
        data-pinned={!!props.pinnedProject}
        ref={row}
      >
        <SidebarDragItem item={projectRef(group().project)} target={projectDrop(group())}>
          <div
            ref={heading}
            data-slot="session-navigation-project-heading"
            onContextMenu={(event) => {
              if (actions.editor) return
              event.preventDefault()
              openMenu(event.clientX, event.clientY)
            }}
          >
            <Show
              when={!actions.editor}
              fallback={
                <div data-slot="session-navigation-project-editor">
                  <ProjectIcon
                    project={group().project}
                    remote={!ServerConnection.local(server())}
                    open={isExpanded(group().key)}
                  />
                  <InlineInput
                    ref={input}
                    aria-label={language.t("common.rename")}
                    dir="auto"
                    value={actions.editor?.draft ?? ""}
                    disabled={actions.editor?.saving}
                    onInput={(event) => setActions("editor", "draft", event.currentTarget.value)}
                    onKeyDown={(event) => {
                      event.stopPropagation()
                      if (event.isComposing || event.keyCode === 229) return
                      if (event.key === "Enter") {
                        event.preventDefault()
                        void saveEditor()
                      }
                      if (event.key !== "Escape") return
                      event.preventDefault()
                      closeEditor()
                      requestAnimationFrame(() => toggle?.focus())
                    }}
                    onBlur={closeEditor}
                  />
                </div>
              }
            >
              <button
                ref={toggle}
                type="button"
                data-action="session-navigation-project-toggle"
                aria-expanded={isExpanded(group().key)}
                aria-controls={id}
                title={group().project.worktree}
                onClick={() => sidebar.setProjectExpanded(projectRef(group().project), !isExpanded(group().key))}
                onKeyDown={(event) => {
                  if (event.key !== "ContextMenu" && (event.key !== "F10" || !event.shiftKey)) return
                  event.preventDefault()
                  const bounds = event.currentTarget.getBoundingClientRect()
                  openMenu(bounds.left + 12, bounds.bottom)
                }}
              >
                <ProjectIcon
                  project={group().project}
                  remote={!ServerConnection.local(server())}
                  open={isExpanded(group().key)}
                />
                <span data-slot="session-navigation-project-name" dir="auto">
                  {group().name}
                </span>
                <Show when={!isExpanded(group().key) && status() !== "idle"}>
                  <span
                    data-slot="session-navigation-project-status"
                    data-status={status()}
                    title={navigationStatusLabel(status(), language)}
                  >
                    <span data-slot="session-navigation-status-dot" aria-hidden="true" />
                    <span class="sr-only">{navigationStatusLabel(status(), language)}</span>
                  </span>
                </Show>
              </button>
            </Show>
            <Menu placement="bottom-end" gutter={6} modal={false}>
              <Tooltip
                value={language.t("session.navigation.projectOptions", { project: group().name })}
                placement="bottom"
              >
                <Menu.Trigger
                  as={IconButton}
                  data-action="session-navigation-project-menu"
                  variant="ghost-muted"
                  size="small"
                  icon={<Icon name="outline-dots" size="small" />}
                  aria-label={language.t("session.navigation.projectOptions", { project: group().name })}
                />
              </Tooltip>
              <SidebarMenuPortal>
                <Menu.Content>{menuItems()}</Menu.Content>
              </SidebarMenuPortal>
            </Menu>
            <Tooltip
              value={language.t("session.navigation.newInProject", { project: group().name })}
              placement="bottom"
            >
              <IconButton
                data-action="session-navigation-project-new"
                variant="ghost-muted"
                size="small"
                icon={<Icon name="new-session" />}
                aria-label={language.t("session.navigation.newInProject", { project: group().name })}
                disabled={!connected()}
                onClick={() => newInProject(group().project.worktree)}
              />
            </Tooltip>
            <Menu
              modal={false}
              placement="bottom-start"
              gutter={2}
              open={!!actions.menu}
              onOpenChange={(open) => {
                if (!open) setActions("menu", undefined)
              }}
            >
              <Menu.Trigger
                as="span"
                aria-hidden="true"
                tabIndex={-1}
                class="pointer-events-none absolute size-px"
                style={{ left: `${actions.menu?.x ?? 0}px`, top: `${actions.menu?.y ?? 0}px` }}
              />
              <SidebarMenuPortal>
                <Menu.Content
                  onInteractOutside={() => {
                    outside = true
                  }}
                  onCloseAutoFocus={(event) => {
                    event.preventDefault()
                    const restore = !outside && !actions.editor
                    outside = false
                    if (restore) requestAnimationFrame(() => toggle?.focus())
                  }}
                >
                  {menuItems()}
                </Menu.Content>
              </SidebarMenuPortal>
            </Menu>
          </div>
        </SidebarDragItem>
        <SidebarDropZone name="project-sessions" target={sessionDrop(group())}>
          <div id={id} data-slot="session-navigation-project-sessions" hidden={!isExpanded(group().key)}>
            <Show when={isExpanded(group().key)}>
              <Show
                when={group().records.length === 0 && connected() && !sessionLoad.isPending && !sessionLoad.isError}
              >
                <p data-slot="session-navigation-project-empty">
                  {language.t(
                    group().hasPinnedSessions ? "session.navigation.noUnpinned" : "session.navigation.noRecent",
                  )}
                </p>
              </Show>
              <Key each={group().records} by={(record) => record.session.id}>
                {(record) => (
                  <SidebarDragItem
                    item={sessionSidebarRef(ServerConnection.key(server()!), record())}
                    project={projectRef(group().project)}
                    target={sessionDrop(group(), sessionSidebarRef(ServerConnection.key(server()!), record()))}
                  >
                    <SessionNavigationRow
                      record={record()}
                      server={ServerConnection.key(server()!)}
                      onMove={(offset) => moveSession(group(), record().session.id, offset)}
                      canMoveUp={group().records[0]?.session.id !== record().session.id}
                      canMoveDown={group().records.at(-1)?.session.id !== record().session.id}
                      status={stateById().get(record().session.id)?.() ?? "idle"}
                      active={currentSession() === record().session.id}
                      onOpen={(event) => open(record(), event)}
                    />
                  </SidebarDragItem>
                )}
              </Key>
            </Show>
          </div>
        </SidebarDropZone>
      </section>
    )
  }
  return { groups, projectRef, Heading, Status, Empty, Project }
}

function RemoveProjectDialog(props: { name: string; remove: () => void }) {
  const dialog = useDialog()
  const language = useLanguage()
  return (
    <Dialog
      fit
      containerClass="sair-project-dialog-container sair-confirm-dialog-container"
      class="sair-project-dialog"
    >
      <DialogHeader>
        <DialogTitleGroup
          title={language.t("session.navigation.removeProject.title", { name: props.name })}
          description={language.t("session.navigation.removeProject.description")}
        />
      </DialogHeader>
      <DialogFooter>
        <Button autofocus variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </Button>
        <Button
          variant="contrast"
          onClick={() => {
            props.remove()
            dialog.close()
          }}
        >
          {language.t("session.navigation.removeProject")}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
